import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { 
  ArrowLeft, 
  User, 
  UserPlus,
  Search,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import '../../styles/CreateUser.css';

const CreateUser = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Duplicate check states
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateResults, setDuplicateResults] = useState([]);
  const [showDuplicateResults, setShowDuplicateResults] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    nickname: '',
    handle: '',
    user_type: 'artist', // Default to artist
    test_account: false
  });

  // Auto-generate handle when first_name or last_name changes
  const generateHandle = (firstName, lastName) => {
    if (!firstName && !lastName) return '';
    const baseHandle = `${firstName}${lastName}`.toLowerCase().replace(/[^a-z0-9]/g, '');
    return baseHandle;
  };

  // Auto-generate email when first_name or last_name changes
  const generateEmail = (firstName, lastName) => {
    if (!firstName && !lastName) return '';
    const emailPrefix = `${firstName}${lastName}`.toLowerCase().replace(/[^a-z0-9]/g, '');
    return `${emailPrefix}@vibrantcast.com`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-populate handle, nickname, and email when first_name or last_name changes
      // Only auto-populate if fields are empty OR if they match what would be auto-generated
      if (field === 'first_name' || field === 'last_name') {
        const currentFirstName = field === 'first_name' ? value : prev.first_name;
        const currentLastName = field === 'last_name' ? value : prev.last_name;
        
        // Generate expected values based on current state
        const expectedHandle = generateHandle(prev.first_name, prev.last_name);
        const expectedEmail = generateEmail(prev.first_name, prev.last_name);
        const expectedNickname = prev.first_name && prev.last_name 
          ? `${prev.first_name} ${prev.last_name}` 
          : prev.first_name || prev.last_name || '';
        
        // Auto-populate handle if it's empty or if it matches the current auto-generated value
        if (!prev.handle || prev.handle === expectedHandle) {
          newData.handle = generateHandle(currentFirstName, currentLastName);
        }
        
        // Auto-populate email if it's empty or if it matches the current auto-generated value
        if (!prev.email || prev.email === expectedEmail) {
          newData.email = generateEmail(currentFirstName, currentLastName);
        }
        
        // Auto-populate nickname if it's empty or if it matches the current auto-generated value
        if (!prev.nickname || prev.nickname === expectedNickname) {
          if (currentFirstName && currentLastName) {
            newData.nickname = `${currentFirstName} ${currentLastName}`;
          } else if (currentFirstName) {
            newData.nickname = currentFirstName;
          } else {
            newData.nickname = currentLastName || '';
          }
        }
      }
      
      return newData;
    });
  };

  const validateForm = () => {
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.handle || !formData.nickname || !formData.user_type) {
      setError('Please fill in all required fields (First Name, Last Name, Email, Handle, Public Name, User Type)');
      return false;
    }

    // Handle validation
    const handleRegex = /^[a-zA-Z0-9_-]+$/;
    if (!handleRegex.test(formData.handle)) {
      setError('Handle can only contain letters, numbers, underscores, and hyphens');
      return false;
    }

    if (formData.handle.length < 3) {
      setError('Handle must be at least 3 characters long');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const checkForDuplicates = async () => {
    if (!formData.first_name && !formData.last_name && !formData.email.includes('@') || formData.email.includes('@vibrantcast.com')) {
      setError('Please enter at least a first name, last name, or non-VibrantCast email to check for duplicates');
      return;
    }

    setCheckingDuplicates(true);
    setError('');
    setDuplicateResults([]);

    try {
      let query = supabase.from('users').select('uuid, first_name, last_name, email, nickname, handle, created_at');
      
      // Build the search conditions
      const conditions = [];
      
      if (formData.first_name) {
        conditions.push(`first_name.ilike.%${formData.first_name}%`);
      }
      
      if (formData.last_name) {
        conditions.push(`last_name.ilike.%${formData.last_name}%`);
      }
      
      // Only search by email if it's not a @vibrantcast.com email (since those are temp emails)
      if (formData.email && !formData.email.includes('@vibrantcast.com')) {
        conditions.push(`email.ilike.%${formData.email}%`);
      }

      if (conditions.length === 0) {
        setError('Please enter search criteria (name or non-VibrantCast email)');
        return;
      }

      // Search using OR conditions
      const { data: users, error } = await query.or(conditions.join(','));

      if (error) {
        throw error;
      }

      setDuplicateResults(users || []);
      setShowDuplicateResults(true);

      if (users && users.length > 0) {
        setError(`Found ${users.length} potentially matching user(s). Please review before creating.`);
      } else {
        setSuccess('No matching users found. Safe to create new user.');
      }

    } catch (error) {
      console.error('Error checking for duplicates:', error);
      setError(`Error checking for duplicates: ${error.message}`);
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const closeDuplicateResults = () => {
    setShowDuplicateResults(false);
    setDuplicateResults([]);
    setError('');
    setSuccess('');
  };

  const viewUserProfile = (userId) => {
    window.open(`/admin/users/${userId}`, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Step 1: Check if email already exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('email')
        .eq('email', formData.email)
        .maybeSingle();

      if (existingUser) {
        throw new Error('A user with this email already exists');
      }

      // Step 2: Check if handle is unique
      const { data: existingHandle } = await supabase
        .from('users')
        .select('handle')
        .eq('handle', formData.handle)
        .maybeSingle();

      if (existingHandle) {
        throw new Error('Handle is already taken. Please choose a different handle.');
      }

      // Step 3: Call edge function to create auth + profile in sync
      const { data: result, error: fnError } = await supabase.functions.invoke('admin_create_user', {
        body: {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          nickname: formData.nickname,
          handle: formData.handle,
          commissions: false,
        },
      });

      if (fnError) {
        throw new Error(fnError.message || 'Failed to create user');
      }

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to create user');
      }

      const userData = result.user;

      setSuccess('User profile created successfully! Redirecting to user profile...');
      
      // Navigate to the new user's profile page after a brief delay
      setTimeout(() => {
        navigate(`/admin/users/${userData.uuid}`);
      }, 1500);

    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/users');
  };

  return (
    <div className="create-user">
      {/* Main Form */}
      <form onSubmit={handleSubmit} className="create-user-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Basic Information */}
        <div className="form-section">
          <div className="section-header">
            <button 
              type="button" 
              className="back-button"
              onClick={handleCancel}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Users
            </button>
            <h3>Basic Information</h3>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">First Name *</label>
              <input
                type="text"
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter first name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="last_name">Last Name *</label>
              <input
                type="text"
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter email address"
              />
              <small>Auto-populated as firstname.lastname@vibrantcast.com. Change to actual email when known.</small>
            </div>
            <div className="form-group">
              <label htmlFor="nickname">Public Name *</label>
              <input
                type="text"
                id="nickname"
                value={formData.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter public/artist name"
              />
              <small>This is the name that will be displayed publicly. Auto-populated from first and last name.</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="handle">Handle *</label>
              <input
                type="text"
                id="handle"
                value={formData.handle}
                onChange={(e) => handleInputChange('handle', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter unique handle"
              />
              <small>Auto-populated from first and last name. Must be unique.</small>
            </div>
            <div className="form-group">
              {/* Empty group to maintain grid layout */}
            </div>
          </div>

          {/* Duplicate Check Section */}
          <div className="form-row">
            <div className="form-group">
              <button
                type="button"
                className="btn-secondary duplicate-check-btn"
                onClick={checkForDuplicates}
                disabled={checkingDuplicates || loading}
              >
                {checkingDuplicates ? (
                  <>
                    <div className="spinner"></div>
                    Checking...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    Check for Existing Users
                  </>
                )}
              </button>
              <small>Search for existing users with the same name or email to avoid duplicates.</small>
            </div>
            <div className="form-group">
              {/* Empty group to maintain grid layout */}
            </div>
          </div>
        </div>

        {/* Duplicate Results Modal */}
        {showDuplicateResults && (
          <div className="duplicate-results-overlay">
            <div className="duplicate-results-modal">
              <div className="duplicate-results-header">
                <h3>Potential Duplicate Users Found</h3>
                <button
                  type="button"
                  className="close-btn"
                  onClick={closeDuplicateResults}
                >
                  ×
                </button>
              </div>
              
              <div className="duplicate-results-content">
                {duplicateResults.length === 0 ? (
                  <div className="no-duplicates">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                    <p>No matching users found. Safe to create new user.</p>
                  </div>
                ) : (
                  <>
                    <div className="duplicate-warning">
                      <AlertTriangle className="w-6 h-6 text-yellow-500" />
                      <p>Found {duplicateResults.length} potentially matching user(s). Please review before creating a new account.</p>
                    </div>
                    
                    <div className="duplicate-users-list">
                      {duplicateResults.map((user) => (
                        <div key={user.uuid} className="duplicate-user-item">
                          <div className="user-info">
                            <h4>{user.first_name} {user.last_name}</h4>
                            <p><strong>Email:</strong> {user.email}</p>
                            <p><strong>Public Name:</strong> {user.nickname}</p>
                            <p><strong>Handle:</strong> @{user.handle}</p>
                            <p><strong>Created:</strong> {new Date(user.created_at).toLocaleDateString()}</p>
                          </div>
                          <button
                            type="button"
                            className="btn-primary view-user-btn"
                            onClick={() => viewUserProfile(user.uuid)}
                          >
                            View Profile
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              
              <div className="duplicate-results-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeDuplicateResults}
                >
                  Close
                </button>
                {duplicateResults.length === 0 && (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={closeDuplicateResults}
                  >
                    Proceed with Creation
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Account Settings */}
        <div className="form-section">
          <h3>Account Settings</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="user_type">User Type *</label>
              <select
                id="user_type"
                value={formData.user_type}
                onChange={(e) => handleInputChange('user_type', e.target.value)}
                required
                disabled={loading}
              >
                <option value="collector">Collector</option>
                <option value="artist">Artist</option>
              </select>
              <small>Determines the default dashboard and features available to the user.</small>
            </div>
            <div className="form-group">
              {/* Empty group to maintain grid layout */}
            </div>
          </div>
          
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.test_account}
                onChange={(e) => handleInputChange('test_account', e.target.checked)}
                disabled={loading}
              />
              Test Account
            </label>
            <small>Mark this user as a test account (for development/testing purposes)</small>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner"></div>
                Creating Account...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                Create User
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateUser;